import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import useLanguageContext from '../../hooks/useLanguageContext';
import SupportedLanguages from '../../setup/intl/enums';
import {
  IconBox,
  IconChevronLeft,
  IconChevronRight,
  IconRefresh,
  IconBank,
} from '../../assets/svg/icons';
import ScCard from '../../components/atoms/ScCard';
import ScSectionHeader from '../../components/molecules/ScSectionHeader';
import ScButton from '../../components/atoms/ScButton';
import { ButtonKind, ButtonSize } from '../../components/atoms/Button/enums';
import Badge from '../../components/atoms/Badge';
import { BadgeTone } from '../../components/atoms/Badge/enums';
import { Product, Supplier } from './types';
import { iqd } from './format';
import { activeTierIndex, maxOrderableQty, snapToMultiple, tierPrice } from './pricing';
import { categoryLabel, cityLabel, unitForms } from './liveData';
import { useQuote } from './hooks';
import { clampToStock, hasKnownStock, isSoldOut, stockState } from './availability';
import { BrandTile, ProductImage, StarRow } from './components';
import {
  StyledBackChip,
  StyledBreadcrumb,
  StyledCrumbCurrent,
  StyledCrumbMuted,
  StyledCrumbSep,
  StyledOrderGrid,
  StyledStickyCol,
  StyledStock,
} from './styles';
import {
  StyledAssurance,
  StyledAssuranceList,
  StyledBadgeRow,
  StyledBuyBtns,
  StyledBuyPrice,
  StyledBuyPriceBig,
  StyledDesc,
  StyledDetailLeft,
  StyledDetailTitleCol,
  StyledFreightHint,
  StyledGalleryGrid,
  StyledHeroImg,
  StyledKV,
  StyledMaxNote,
  StyledQuickSet,
  StyledQuickSets,
  StyledSpecGrid,
  StyledStepBtn,
  StyledStepInput,
  StyledStepper,
  StyledSubtotalRow,
  StyledSupplierBody,
  StyledSupplierMeta,
  StyledSupplierName,
  StyledThumb,
  StyledThumbRow,
  StyledTierCard,
  StyledTierLabel,
  StyledTierPrice,
  StyledTierRow,
  StyledTierUnit,
} from './styles2';

interface ScreenProductProps {
  product: Product;
  supplier: Supplier | null;
  initialQty: number;
  freightEnabled?: boolean;
  onBack: () => void;
  onAddToCart: (p: Product, qty: number) => void;
  onBuyNow: (p: Product, qty: number) => void;
}

const ScreenProduct: React.FC<ScreenProductProps> = ({
  product: p,
  supplier,
  initialQty,
  freightEnabled,
  onBack,
  onAddToCart,
  onBuyNow,
}) => {
  const intl = useIntl();
  const { language } = useLanguageContext();
  const isAr = language === SupportedLanguages.Arabic;
  const name = isAr ? p.nameAr || p.name : p.name;
  const description = isAr ? p.descAr || p.desc : p.desc;
  const moq = Math.max(p.moq, 1);
  const [qty, setQty] = useState<number>(clampToStock(p, initialQty || moq));
  const [clamped, setClamped] = useState(false);
  const [snappedNote, setSnappedNote] = useState(false);
  const [inputStr, setInputStr] = useState<string | null>(null);
  const unit = tierPrice(p, qty);
  const activeIdx = activeTierIndex(p, qty);
  const soldOut = isSoldOut(p);
  const ss = stockState(p);
  const stockUnitForms = unitForms(p.stockUnit, language);
  const cur = intl.formatMessage({ id: 'marketplace.currency', defaultMessage: 'IQD' });
  const stockMax = hasKnownStock(p) ? (p.stock as number) : undefined;
  const atMax = stockMax != null && qty >= stockMax;

  // Surface SERVER-OWNED freight EARLY (the #1 sticker-shock risk). Quote this
  // single product×qty; show the policy flat + free-over from freightPolicy.
  // No policy (loading/error/mock) → no freight line at all — we never
  // fabricate a number the server didn't return.
  const { quote: freightQuote } = useQuote([{ product: p, qty }], !!freightEnabled);
  const policy = freightQuote?.freightPolicy;
  const flatFreight = policy?.flatIqd ?? null;
  const freeOver = policy && policy.freeOverIqd > 0 ? policy.freeOverIqd : undefined;

  // Snap to MOQ multiple, clamp to stock, and surface both visibly. A stock
  // cap must itself land on a multiple (stock 20, MOQ 12 → 12, not 20).
  // When 0 < stock < moq ('low', not sold-out) the raw stock clamp commits
  // as-is — a pre-existing product gap; the cart applies the same guard, so
  // PDP and cart stay in parity either way.
  const applyQty = (next: number) => {
    const { qty: snappedQty, snapped } = snapToMultiple(next, moq);
    setSnappedNote(snapped);
    let capped = clampToStock(p, snappedQty);
    if (capped !== snappedQty && stockMax != null && stockMax >= moq) {
      capped = maxOrderableQty(stockMax, moq);
    }
    if (stockMax != null && snappedQty > stockMax) {
      setClamped(true);
      setTimeout(() => setClamped(false), 2400);
    }
    setQty(capped);
    setInputStr(null);
  };

  // Commit a typed draft (blur/Enter) — only when a draft exists, so a bare
  // blur (e.g. mousedown on Add to cart) never re-runs applyQty and unmounts
  // the snap hint mid-click. NaN-safe: "0" must flow through snapToMultiple
  // (→ moq, snapped:true) rather than being silently rewritten by `|| moq`.
  const commitInput = () => {
    if (soldOut || inputStr == null) return;
    const n = parseInt(inputStr, 10);
    applyQty(Number.isNaN(n) ? moq : n);
  };

  return (
    <>
      <StyledBreadcrumb>
        <StyledBackChip type="button" onClick={onBack}>
          <IconChevronLeft />
          <span>{intl.formatMessage({ id: 'mainNavigation.marketplace', defaultMessage: 'Marketplace' })}</span>
        </StyledBackChip>
        <StyledCrumbSep><IconChevronRight /></StyledCrumbSep>
        <StyledCrumbMuted>{categoryLabel(p.cat, language)}</StyledCrumbMuted>
        <StyledCrumbSep><IconChevronRight /></StyledCrumbSep>
        <StyledCrumbCurrent>{name}</StyledCrumbCurrent>
      </StyledBreadcrumb>

      <StyledOrderGrid $side="400px">
        {/* LEFT */}
        <StyledDetailLeft>
          <StyledGalleryGrid>
            <div>
              <StyledHeroImg $hue={p.hue}>
                <ProductImage product={p} />
              </StyledHeroImg>
              {(p.images?.length ?? 0) >= 2 && (
                <StyledThumbRow>
                  {p.images!.map((src, i) => (
                    <StyledThumb
                      key={src}
                      as="img"
                      src={src}
                      alt={`${name} — ${i + 1}`}
                      $hue={p.hue}
                      $active={i === 0}
                    />
                  ))}
                </StyledThumbRow>
              )}
            </div>
            <StyledDetailTitleCol>
              <StyledBadgeRow>
                <Badge tone={BadgeTone.Neutral}>{categoryLabel(p.cat, language)}</Badge>
                {p.rating > 0 && (
                  <>
                    <StarRow r={p.rating} />
                    <span className="muted" style={{ fontSize: 'var(--t-xs)' }}>
                      {intl.formatMessage({ id: 'marketplace.reviewsN', defaultMessage: '({reviews} reviews)' }, { reviews: p.reviews })}
                    </span>
                  </>
                )}
                {p.sold && (
                  <span className="muted" style={{ fontSize: 'var(--t-xs)' }}>
                    {intl.formatMessage({ id: 'marketplace.soldN', defaultMessage: '{sold} sold' }, { sold: p.sold })}
                  </span>
                )}
              </StyledBadgeRow>
              <h1 className="h2" style={{ marginBottom: 12 }}>{name}</h1>
              <StyledDesc>{description}</StyledDesc>

              <div className="eyebrow" style={{ marginBottom: 8 }}>{intl.formatMessage({ id: 'marketplace.volumePricing', defaultMessage: 'Volume pricing' })}</div>
              <StyledTierRow>
                {p.tiers.map((t, i) => {
                  const active = i === activeIdx;
                  const nextMin = p.tiers[i + 1]?.min;
                  return (
                    <StyledTierCard key={t.min} $active={active} onClick={() => applyQty(t.min)}>
                      <StyledTierLabel className="tnum">
                        {nextMin
                          ? intl.formatMessage({ id: 'marketplace.tierRangeUnit', defaultMessage: '{range} {unit}' }, { range: <bdi key="r" dir="ltr">{`${t.min}–${nextMin - 1}`}</bdi>, unit: stockUnitForms.unitPlural })
                          : intl.formatMessage({ id: 'marketplace.tierFromUnit', defaultMessage: '{range} {unit}' }, { range: <bdi key="r" dir="ltr">{`${t.min}+`}</bdi>, unit: stockUnitForms.unitPlural })}
                      </StyledTierLabel>
                      <StyledTierPrice $active={active} className="mono tnum">{iqd(t.price)}</StyledTierPrice>
                      <StyledTierUnit>{intl.formatMessage({ id: 'marketplace.iqdPerUnit', defaultMessage: 'IQD / {unit}' }, { unit: stockUnitForms.unit })}</StyledTierUnit>
                    </StyledTierCard>
                  );
                })}
              </StyledTierRow>

              <div className="eyebrow" style={{ marginBottom: 8 }}>{intl.formatMessage({ id: 'marketplace.specifications', defaultMessage: 'Specifications' })}</div>
              <StyledSpecGrid>
                <StyledKV><span>{intl.formatMessage({ id: 'marketplace.spec.brand', defaultMessage: 'Brand' })}</span><span>{p.brand}</span></StyledKV>
                <StyledKV><span>{intl.formatMessage({ id: 'marketplace.spec.category', defaultMessage: 'Category' })}</span><span>{categoryLabel(p.cat, language)}</span></StyledKV>
                <StyledKV><span>{intl.formatMessage({ id: 'marketplace.spec.moq', defaultMessage: 'MOQ' })}</span><span className="tnum">{intl.formatMessage({ id: 'marketplace.countUnit', defaultMessage: '{n, plural, one {# {unit}} other {# {unitPlural}}}' }, { n: p.moq, ...stockUnitForms })}</span></StyledKV>
                {p.lead && <StyledKV><span>{intl.formatMessage({ id: 'marketplace.spec.lead', defaultMessage: 'Lead time' })}</span><span>{p.lead}</span></StyledKV>}
                <StyledKV><span>{intl.formatMessage({ id: 'marketplace.spec.inStock', defaultMessage: 'In stock' })}</span><span className="tnum">{p.stock != null ? intl.formatMessage({ id: 'marketplace.countUnit', defaultMessage: '{n, plural, one {# {unit}} other {# {unitPlural}}}' }, { n: p.stock, ...stockUnitForms }) : intl.formatMessage({ id: 'marketplace.stock.onRequest', defaultMessage: 'Availability on request' })}</span></StyledKV>
                <StyledKV><span>{intl.formatMessage({ id: 'marketplace.spec.shipsFrom', defaultMessage: 'Ships from' })}</span><span>{supplier?.city ? cityLabel(supplier.city, language) : '—'}</span></StyledKV>
              </StyledSpecGrid>
            </StyledDetailTitleCol>
          </StyledGalleryGrid>

          {/* supplier */}
          <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
            <ScSectionHeader
              title={intl.formatMessage({ id: 'marketplace.supplier', defaultMessage: 'Supplier' })}
            />
            <StyledSupplierBody>
              <BrandTile brand={{ name: supplier?.name || p.brand, hue: p.hue }} size={56} radius="var(--r-lg)" />
              <StyledSupplierMeta>
                <StyledSupplierName>
                  <span>{supplier?.name || p.brand}</span>
                  <Badge tone={BadgeTone.Success} dot>{intl.formatMessage({ id: 'marketplace.verified', defaultMessage: 'Verified' })}</Badge>
                </StyledSupplierName>
                <div className="muted" style={{ fontSize: 'var(--t-sm)', marginTop: 2 }}>
                  {supplier?.city ? cityLabel(supplier.city, language) : ''}
                </div>
              </StyledSupplierMeta>
              {p.rating > 0 && (
                <div style={{ textAlign: 'end' }}>
                  <StarRow r={p.rating} size={15} />
                  <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{intl.formatMessage({ id: 'marketplace.supplierRating', defaultMessage: 'Supplier rating' })}</div>
                </div>
              )}
            </StyledSupplierBody>
          </ScCard>
        </StyledDetailLeft>

        {/* RIGHT — buy box */}
        <StyledStickyCol>
          <ScCard pad={false} style={{ padding: 'var(--s-5)' }}>
            <StyledBuyPrice>
              <StyledBuyPriceBig className="mono tnum">{iqd(unit)}</StyledBuyPriceBig>
              <span className="muted" style={{ fontSize: 'var(--t-sm)' }}>{intl.formatMessage({ id: 'marketplace.iqdPerUnit', defaultMessage: 'IQD / {unit}' }, { unit: stockUnitForms.unit })}</span>
            </StyledBuyPrice>
            <div className="muted" style={{ fontSize: 'var(--t-sm)', marginTop: 2 }}>
              {intl.formatMessage({ id: 'marketplace.bestPriceAt', defaultMessage: 'Best price at {n}+ {unit}' }, { n: p.tiers[p.tiers.length - 1]?.min ?? p.moq, unit: stockUnitForms.unitPlural })}
            </div>

            <StyledStock $low={ss === 'low'} className="tnum" style={{ marginTop: 'var(--s-4)' }}>
              {ss === 'unknown'
                ? intl.formatMessage({ id: 'marketplace.stock.onRequest', defaultMessage: 'Availability on request' })
                : ss === 'sold-out'
                ? intl.formatMessage({ id: 'marketplace.stock.soldOut', defaultMessage: 'Sold out' })
                : ss === 'low'
                ? intl.formatMessage({ id: 'marketplace.stock.low', defaultMessage: '{n, plural, one {Only # {unit} left} other {Only # {unitPlural} left}}' }, { n: p.stock, ...stockUnitForms })
                : intl.formatMessage({ id: 'marketplace.stock.available', defaultMessage: '{n, plural, one {# {unit} available} other {# {unitPlural} available}}' }, { n: p.stock, ...stockUnitForms })}
            </StyledStock>

            <div style={{ marginTop: 'var(--s-5)' }}>
              <div className="eyebrow" style={{ marginBottom: 8 }}>
                {intl.formatMessage({ id: 'marketplace.orderQty', defaultMessage: 'Order quantity' })}{' '}
                <span style={{ color: 'var(--muted-2)', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>· {intl.formatMessage({ id: 'marketplace.moqShort', defaultMessage: 'MOQ {n}' }, { n: p.moq })}</span>
              </div>
              <StyledStepper>
                <StyledStepBtn type="button" onClick={() => applyQty(qty - moq)} disabled={soldOut || qty <= moq} aria-label={intl.formatMessage({ id: 'marketplace.decrease', defaultMessage: 'Decrease quantity' })}>−</StyledStepBtn>
                <StyledStepInput
                  value={inputStr ?? qty}
                  readOnly={soldOut}
                  onChange={(e) => { if (!soldOut) setInputStr(e.target.value); }}
                  onBlur={commitInput}
                  onKeyDown={(e) => { if (e.key === 'Enter') commitInput(); }}
                  inputMode="numeric"
                  aria-label={intl.formatMessage({ id: 'marketplace.orderQty', defaultMessage: 'Order quantity' })}
                  aria-disabled={soldOut || undefined}
                  style={soldOut ? { color: 'var(--muted)', background: 'var(--paper-2)' } : undefined}
                />
                <StyledStepBtn type="button" onClick={() => applyQty(qty + moq)} disabled={soldOut || atMax} aria-label={intl.formatMessage({ id: 'marketplace.increase', defaultMessage: 'Increase quantity' })}>+</StyledStepBtn>
              </StyledStepper>
              <StyledQuickSets>
                {p.tiers.map((t) => (
                  <StyledQuickSet key={t.min} type="button" $active={qty === t.min} onClick={() => applyQty(t.min)} className="tnum">{t.min}</StyledQuickSet>
                ))}
              </StyledQuickSets>
              {clamped && stockMax != null && (
                <StyledMaxNote role="status" aria-live="polite" className="tnum">
                  {intl.formatMessage({ id: 'marketplace.stock.max', defaultMessage: '{n, plural, one {Max # {unit} available} other {Max # {unitPlural} available}}' }, { n: stockMax, ...stockUnitForms })}
                </StyledMaxNote>
              )}
              {snappedNote && moq > 1 && (
                <span className="muted" role="status" style={{ fontSize: 'var(--t-xs)' }}>
                  {intl.formatMessage(
                    { id: 'marketplace.moqMultiples', defaultMessage: 'Sold in multiples of {moq}' },
                    { moq }
                  )}
                </span>
              )}
            </div>

            <StyledSubtotalRow>
              <span className="muted" style={{ fontSize: 'var(--t-sm)' }}>{intl.formatMessage({ id: 'marketplace.subtotalUnit', defaultMessage: 'Subtotal · {n, plural, one {# {unit}} other {# {unitPlural}}}' }, { n: qty, ...stockUnitForms })}</span>
              <span style={{ fontSize: 'var(--t-2xl)', fontWeight: 700 }}>
                <span className="mono tnum">{iqd(unit * qty)}</span>{' '}
                <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)', fontWeight: 500, fontFamily: 'var(--font-ar, inherit)' }}>{cur}</span>
              </span>
            </StyledSubtotalRow>

            {/* "Free delivery" only for an explicit zero-freight quote; the policy
                line only when the server returned a flat rate. No policy detail →
                render nothing — never invent a number (P0 M-01). */}
            {freightQuote?.freightIqd === 0 ? (
              <StyledFreightHint>
                <IconBox />
                <span>{intl.formatMessage({ id: 'marketplace.freight.free', defaultMessage: 'Free delivery' })}</span>
              </StyledFreightHint>
            ) : flatFreight !== null ? (
              <StyledFreightHint>
                <IconBox />
                <span>
                  {freeOver
                    ? intl.formatMessage(
                        { id: 'marketplace.freight.policyFree', defaultMessage: '+ {flat} IQD freight per order · free over {free} IQD' },
                        { flat: <b key="flat">{iqd(flatFreight)}</b>, free: <b key="free">{iqd(freeOver)}</b> }
                      )
                    : intl.formatMessage(
                        { id: 'marketplace.freight.policy', defaultMessage: '+ {flat} IQD freight per order' },
                        { flat: <b key="flat">{iqd(flatFreight)}</b> }
                      )}
                </span>
              </StyledFreightHint>
            ) : null}

            <StyledBuyBtns>
              <ScButton kind={ButtonKind.Primary} size={ButtonSize.Large} full icon={IconBox} disabled={soldOut} onClick={() => onAddToCart(p, qty)}>
                {soldOut ? intl.formatMessage({ id: 'marketplace.stock.soldOut', defaultMessage: 'Sold out' }) : intl.formatMessage({ id: 'marketplace.addToCart', defaultMessage: 'Add to cart' })}
              </ScButton>
              <ScButton kind={ButtonKind.Outline} size={ButtonSize.Large} full disabled={soldOut} onClick={() => onBuyNow(p, qty)}>
                {intl.formatMessage({ id: 'marketplace.buyNow', defaultMessage: 'Buy now' })}
              </ScButton>
            </StyledBuyBtns>

            <StyledAssuranceList>
              <StyledAssurance><span><IconBox /></span><span>{intl.formatMessage({ id: 'marketplace.assurance.ships', defaultMessage: 'Ships into your Sandoog warehouse' })}</span></StyledAssurance>
              <StyledAssurance><span><IconRefresh /></span><span>{intl.formatMessage({ id: 'marketplace.assurance.quality', defaultMessage: 'Quality-checked on arrival' })}</span></StyledAssurance>
              <StyledAssurance><span><IconBank /></span><span>{intl.formatMessage({ id: 'marketplace.assurance.pay', defaultMessage: 'Pay by wallet, transfer, or net-30 terms' })}</span></StyledAssurance>
            </StyledAssuranceList>
          </ScCard>
        </StyledStickyCol>
      </StyledOrderGrid>
    </>
  );
};

export default ScreenProduct;
